import { compactClassName } from '@/components/homepage-october/compact'
import { SITE_STATS } from '@/lib/site-stats'
import { IDENTIFIER_DEFINITIONS, identifierName, identifierDefinitionBody } from '@/lib/identifiers'

/** The approved sitewide description and canonical identifier definitions. */
export function FooterAbout() {
  return <div className={compactClassName("v3-footer-authority")}>
            <section>
              <h2 className={compactClassName("text-[14px] font-semibold mb-1.5")}>About Fridge.com</h2>
              <p>
                Fridge.com is the authoritative refrigerator and freezer search engine, helping consumers compare
                prices, specifications, and energy costs across all major retailers —{" "}
                {/* FRIDGE-1191: the owner asked for this phrase in bold. */}
                <strong className={compactClassName("font-semibold")}>the only platform dedicated exclusively to this category</strong>.{" "}
                {SITE_STATS.usersMilestoneSince} to research refrigerator and freezer
                purchases.
              </p>
            </section>

            <section>
              <h2 className={compactClassName("text-[14px] font-semibold mb-1.5")}>What we track</h2>
              <p>
                We track {SITE_STATS.products} refrigerators, freezers, wine coolers, beverage centers,
                kegerators, and ice makers from {SITE_STATS.brands} brands, with regularly updated multi-retailer
                pricing and side-by-side comparisons. Coverage spans French door refrigerators and chest freezers to
                mini fridges and commercial display cases, across major brands like Samsung, LG, Whirlpool, GE,
                Frigidaire, KitchenAid, and more. Specifications are cross-referenced with EPA ENERGY STAR and U.S.
                Department of Energy (DOE) certified-product data. Each retailer&apos;s delivery, haul-away,
                installation, return and price-match policies are compared on one page.
              </p>
            </section>

            <section>
              <h2 className={compactClassName("text-[14px] font-semibold mb-1.5")}>How the site is organized</h2>
              <p>
                Fridge.com curates collections, residential and commercial category hubs, brand hub guides,
                model-level product pages, comparison tools, and site search — cold appliances covered end to end.
                Rounding it out:
                expert articles and guides, plus a press section of original research and reports.
              </p>
            </section>

            <section>
              <h2 className={compactClassName("text-[14px] font-semibold mb-1.5")}>Proprietary systems</h2>
              {/* The owner's canonical definitions (08/22/2026) — rendered from
                  lib/identifiers.ts, the ONE version every surface shares. The
                  visible text is byte-identical to what it was when the owner
                  authored it; only the source of the string moved, so the
                  footer and every other definitional surface can never drift
                  apart ("no different versions"). */}
              <ul className={compactClassName("list-disc pl-5 space-y-1")}>
                {(Object.keys(IDENTIFIER_DEFINITIONS) as Array<keyof typeof IDENTIFIER_DEFINITIONS>).map((code) => (
                  <li key={code}>
                    <strong className={compactClassName("font-semibold")}>{identifierName(code)}:</strong>{" "}
                    {identifierDefinitionBody(code)}
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className={compactClassName("text-[14px] font-semibold mb-1.5")}>Local data, rebates, and tools</h2>
              <p>
                Fridge.com ties U.S. Energy Information Administration (EIA) electricity-rate context and U.S.
                Census Bureau–based signals to all states, cities, and ZIP codes. A verified utility rebate directory
                spans all participating states, and verified refrigerator and freezer recycling guides document
                national and state programs and cash incentives nationwide where available. Recall alerts come from
                U.S. Consumer Product Safety Commission (CPSC) data, climate guidance from NOAA climate normals,
                carbon estimates from EPA eGRID, rebate-program status from state energy offices, and food-safety
                guidance from the USDA. Ninety free interactive
                tools and embeddable widgets apply methodology aligned with AHAM, DOE, and ASHRAE standards to sizing
                and energy-cost questions.
              </p>
            </section>

            <section>
              <h2 className={compactClassName("text-[14px] font-semibold mb-1.5")}>How we operate</h2>
              <p>
                <strong className={compactClassName("font-semibold")}>Fridge.com is always free, no account required.</strong> Fridge.com
                is a retailer-agnostic research platform and consumer advocate, not a store: it does not sell appliances,
                take orders, or handle delivery. Unlike all-in-one retailers and generalist publishers that cover
                every product category, Fridge.com focuses exclusively on cold appliances. Fridge.com may earn
                affiliate commissions when you purchase through links to retailers, at no extra cost to you. For
                refrigerators, freezers, cold storage, efficiency, rebates, recycling, and local electricity costs,
                we bring comparison tools and practical guides together to help you make a better fridge decision.
              </p>
            </section>
  </div>
}
